import {flatten} from 'lodash';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsHeatMapMetricsEnum,
  AnalyticsHeatMapMetricValue,
  AnalyticsScalarMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsSelectedMetrics,
  makeGetAnalyticsMetric,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {
  renderHourWithoutMinutes,
  renderWeekDay,
  renderWeekDayFull,
} from '../../../helpers/analyticsFormatHelpers';
import {
  ContributingSegmentsOptionsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {useAnalyticsUpdateSelectedMetrics} from '../../../helpers/hooks/useAnalyticsUpdateSelectedMetrics';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {BusinessHourLimits} from '../../businessHours/helpers/businessHourLimits';
import {HeatMap} from '../../charts/components/heatmap/heatmap';
import {HeatmapLegend} from '../../charts/components/legend/heatmapLegend';
import {StyledElementWrapper, StyledFlex} from '../../reportViewer/reportCommons';
import {MetricErrorState} from '../charts/common/metricErrorState';
import {ReportElementWrapper} from '../reportElement';
import {HeatMapTopItems} from './heatmapTopItems';
import {ReportHeatMapProps} from './reportHeatMap';

/*
 * Constants.
 */

const CELL_HEIGHT_CONDENSED = 27;
const CELL_HEIGHT_EXPANDED = 30;
const CELL_GAP = 4; // Gap between each cell.

const makeMockData = (localize: Localizer) =>
  flatten(
    Array.from(Array(7), (_, dayIndex) =>
      Array.from(Array(9), (__, hourIndex) => ({
        day: renderWeekDay(dayIndex),
        dayFull: renderWeekDayFull(dayIndex),
        hour: renderHourWithoutMinutes(localize, hourIndex),
        timePeriod: calculateTopItemsTime(localize, hourIndex),
        value: 0,
        key: '',
      })),
    ),
  );

/*
 * Styles.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  width: 224px;
  margin-right: 16px;
`;

const StyledLegendContainer = styled.div`
  ${flexCenter}
  flex: 1;
  justify-content: flex-end;
`;

const StyledHeatmapWrapper = styled(StyledElementWrapper)<{$isExpanded: boolean}>`
  ${(p) =>
    p.$isExpanded &&
    css`
      height: calc(100% - 80px);
      overflow-y: auto;
      overflow-x: hidden;
    `}
`;

const StyledHeatmapSectionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledHeatmapContainer = styled.div`
  width: 70%;
`;

const StyledHeatmapTopItemsContainer = styled.div`
  width: 30%;
`;

/*
 * Props.
 */

export interface ReportHeatMapElementProps extends ReportHeatMapProps {
  filterValue?: PredicateOf<AnalyticsHeatMapMetricValue>;
  onToggleHeatmapExpanded: Handler;
  isHeatmapExpanded: boolean;
  businessHourLimits?: BusinessHourLimits;
}

export interface HeatMapValue {
  key: string;
  day: string;
  dayFull: string;
  hour: string;
  timePeriod: string;
  value: number;
}

/**
 * Component. The actual Heatmap.
 * Used both for the Heatmap report element and the Heatmap popout. The behaviour and UI slightly differ based on this.
 */

// @TODO: Implement events once #4951 is merged.
export const ReportHeatMapElement: FC<ReportHeatMapElementProps> = ({
  isLoading,
  metric,
  title,
  reportSectionId,
  filterValue,
  onToggleHeatmapExpanded,
  isHeatmapExpanded,
  businessHourLimits,
}) => {
  const theme = useUserTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSizeInfo = useSize(containerRef);
  const dispatch = useAppDispatch();
  const localize = useLocalize();

  const {updateSelectedMetrics} = useAnalyticsUpdateSelectedMetrics();

  const [metricData, metricError] = useAppSelector(makeGetAnalyticsMetric(metric));
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);

  const subMetrics = metricData?.subMetrics;
  const values = metricData?.values;

  const defaultSelectedMetric = useMemo(
    () => (subMetrics?.length ? (subMetrics[0].id as AnalyticsScalarMetricsEnum) : undefined), // @TODO submetric casting
    [subMetrics],
  );

  // @TODO: Could we replace `id` from string to AnalyticsScalarMetricsEnum in typings?
  const [selectedMetric, setSelectedMetric] = useState<AnalyticsScalarMetricsEnum | undefined>(
    defaultSelectedMetric,
  );

  // This will trigger when we select a view or change the selected metric.
  useEffect(() => {
    if (selectedMetrics[reportSectionId]) {
      setSelectedMetric(selectedMetrics[reportSectionId][0] as AnalyticsScalarMetricsEnum);
    } else {
      setSelectedMetric(defaultSelectedMetric);
    } // reset to original value.
  }, [defaultSelectedMetric, reportSectionId, selectedMetrics]);

  const heatMapData: HeatMapValue[] = useMemo(() => {
    if (selectedMetric && values) {
      return values
        .filter((value) => (filterValue ? filterValue(value) : true))
        .map((value) => {
          const selectedValue = value.values[selectedMetric];

          return {
            key: value.key,
            day: renderWeekDay(value.day),
            dayFull: renderWeekDayFull(value.day),
            hour: renderHourWithoutMinutes(localize, value.hour),
            timePeriod: calculateTopItemsTime(localize, value.hour),
            value: selectedValue.value,
          };
        });
    }

    if (isLoading) {
      return makeMockData(localize);
    }

    return [];
  }, [selectedMetric, values, isLoading, localize, filterValue]);

  const topItems: HeatMapValue[] = useMemo(() => {
    if (heatMapData) {
      return [...heatMapData].sort((a, b) => b.value - a.value);
    }

    return [];
  }, [heatMapData]);

  const heatmapHeight = useMemo(() => {
    // Assuming we always have data for 7 days if we don't have business hours set.
    const nbDays = !isHeatmapExpanded && businessHourLimits ? businessHourLimits.days.length : 7;
    const nbRows = heatMapData.length / nbDays;
    const cellHeight = isHeatmapExpanded ? CELL_HEIGHT_EXPANDED : CELL_HEIGHT_CONDENSED;

    // We add one row for the header.
    return cellHeight * (nbRows + 1) + CELL_GAP * nbRows;
  }, [businessHourLimits, heatMapData.length, isHeatmapExpanded]);

  const renderDropdown = (modalProps: PopoverTransferProps) => (
    <SimpleDropdown {...modalProps} width={180}>
      {subMetrics?.map((subMetric) => {
        const metricId = subMetric.id as AnalyticsScalarMetricsEnum; // TODO remove cast when model is updated to directly support enum type
        const {label: metricLabel} = getMetricLabels(metricId);
        return (
          <SimpleDropdownItem
            key={metricId}
            isChecked={selectedMetric === metricId}
            onSelect={() => updateSelectedMetrics([metricId], reportSectionId)}
          >
            {metricLabel}
          </SimpleDropdownItem>
        );
      })}
    </SimpleDropdown>
  );

  const onHeatMapValueClick = (metricClicked: AnalyticsScalarMetricsEnum, {key}: HeatMapValue) => {
    if (isHeatmapExpanded) {
      // If heatmap is expanded, we close the dialog first.
      onToggleHeatmapExpanded();
    }

    dispatch(
      setMetricDetailsAction(AnalyticsHeatMapMetricsEnum.INBOX, {
        [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
          [ContributingSegmentsOptionsEnum.ROW]: key,
          [ContributingSegmentsOptionsEnum.SUB_METRIC]: metricClicked,
        },
      }),
    );
  };

  const {label: metricLabel, tooltip: infoTooltip} =
    getMetricLabels<AnalyticsScalarMetricsEnum>(selectedMetric);

  const renderHeatMapContent = () => {
    if (metricError) {
      return (
        <MetricErrorState
          error={metricError}
          height={heatmapHeight}
          width={containerSizeInfo && containerSizeInfo.width}
        />
      );
    }

    if (containerSizeInfo) {
      return (
        <HeatMap
          height={heatmapHeight}
          width={containerSizeInfo.width}
          xAccessor={(d) => d.day}
          yAccessor={(d) => d.hour}
          colorAccessor={(d) => d.value}
          data={heatMapData}
          shouldDisplayValues
          isLoading={isLoading}
          onClick={(value) => selectedMetric && value?.key && onHeatMapValueClick(selectedMetric, value)}
        />
      );
    }

    return null;
  };

  const renderHeatmap = () => (
    <>
      <StyledFlex>
        {subMetrics && subMetrics.length > 1 && (
          <StyledDropdownBoxCoordinator value={metricLabel} renderDropdown={renderDropdown} />
        )}
        <StyledLegendContainer>
          {!isLoading && <HeatmapLegend colorAccessor={(d) => d.value} data={heatMapData} />}
        </StyledLegendContainer>
      </StyledFlex>
      <StyledHeatmapWrapper $isExpanded={isHeatmapExpanded} ref={containerRef}>
        {renderHeatMapContent()}
      </StyledHeatmapWrapper>
    </>
  );

  return (
    <ReportElementWrapper
      title={title ?? getMetricLabels(metric).label}
      reportSectionId={reportSectionId}
      infoTooltip={infoTooltip}
      hasFullHeight={isHeatmapExpanded}
      renderCustom={() => (
        <IconButton
          label={
            isHeatmapExpanded ? (
              <Trans id="analytics-heatmap-close-icon">Close</Trans>
            ) : (
              <Trans id="analytics-heatmap-fullscreen-icon">Expand</Trans>
            )
          }
          iconName={isHeatmapExpanded ? 'popin' : 'popout'}
          color={theme.greys.shade70}
          onClick={onToggleHeatmapExpanded}
        />
      )}
    >
      {!isHeatmapExpanded ? (
        <StyledHeatmapSectionsWrapper>
          <StyledHeatmapContainer>{renderHeatmap()}</StyledHeatmapContainer>
          <StyledHeatmapTopItemsContainer>
            <HeatMapTopItems
              topItems={topItems}
              selectedMetric={selectedMetric}
              height={containerSizeInfo?.height}
              isLoading={isLoading}
              onHeatMapValueClick={onHeatMapValueClick}
            />
          </StyledHeatmapTopItemsContainer>
        </StyledHeatmapSectionsWrapper>
      ) : (
        renderHeatmap()
      )}
    </ReportElementWrapper>
  );
};

// Helpers
function calculateTopItemsTime(localize: Localizer, hour: number) {
  return `${renderHourWithoutMinutes(localize, hour)} - ${renderHourWithoutMinutes(localize, hour + 1)}`;
}
